import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Users, Clock, Wifi, WifiOff, RefreshCw, Eye, List, FileText, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Tracking time window: 09:45 AM – 10:30 PM IST
function isWithinWindow() {
  const d = new Date(Date.now() + (5.5 * 60 - new Date().getTimezoneOffset()) * 60_000);
  const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
  return mins >= 9 * 60 + 45 && mins <= 22 * 60 + 30;
}

// Pune, Maharashtra (DBK Group HQ area)
const PUNE_CENTER = [18.5204, 73.8567];

// ─── Department colour palette ───────────────────────────────────────────────
const DEPT_COLORS = {
  hr: '#8b5cf6',
  marketing: '#10b981',
  finance: '#3b82f6',
  legal: '#f59e0b',
  operations: '#ef4444',
  it: '#6366f1',
  architect: '#14b8a6',
  admin: '#f97316',
  vehicle_maintenance: '#84cc16',
  cmd: '#dc2626',
  default: '#64748b',
};

function deptColor(dept) {
  return DEPT_COLORS[dept] || DEPT_COLORS.default;
}

function timeSince(isoString) {
  if (!isoString) return 'Unknown';
  const diff = (Date.now() - new Date(isoString)) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return `${Math.round(diff / 3600)}h ago`;
}

function formatTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

// ─── Login / Attendance Report (Assistant HR only) ───────────────────────────
function LoginReportView({ token }) {
  const todayIST = () => {
    const d = new Date(Date.now() + (5.5 * 60 + new Date().getTimezoneOffset()) * 60000);
    return d.toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(todayIST());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/location/login-report?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [token, date]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExportExcel = async () => {
    try {
      const res = await fetch(`${API}/location/login-report/export?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Login_Report_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { /* silent fail */ }
  };

  const statusBadge = (row) => {
    if (row.status === 'LATE')
      return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700">LATE</span>;
    if (row.status === 'ON TIME')
      return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700">ON TIME</span>;
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-stone-100 text-stone-500">ABSENT</span>;
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#F8FAFC]" data-testid="login-report-view">
      {/* Controls + summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-purple-600" />
          <h2 className="text-sm font-bold text-stone-800">Daily Login Report</h2>
          <span className="text-[11px] text-stone-400">Auto late-mark after 10:10 AM</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            data-testid="login-report-date"
            className="px-3 py-1.5 text-xs border border-stone-300 rounded-md outline-none focus:ring-2 focus:ring-purple-400 bg-white"
          />
          {report && (
            <button
              onClick={handleExportExcel}
              data-testid="login-report-export-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export Excel
            </button>
          )}
        </div>
      </div>

      {report && (
        <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-4">
          {[
            { l: 'Total', v: report.total, c: 'text-stone-700' },
            { l: 'On Time', v: report.onTime, c: 'text-green-600' },
            { l: 'Late', v: report.late, c: 'text-red-600' },
            { l: 'Absent', v: report.absent, c: 'text-stone-400' },
          ].map(s => (
            <div key={s.l} className="bg-white border border-stone-200 rounded-xl p-3 text-center">
              <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-wide">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone-400 text-sm">Loading login report…</div>
        ) : !report || report.rows.length === 0 ? (
          <div className="p-8 text-center text-stone-400 text-sm">No login data for this date.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="login-report-table">
              <thead>
                <tr className="bg-stone-50 text-stone-500 text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 font-semibold">#</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Employee</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Emp ID</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Department</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Login Time</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Logout Time</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr key={row.email} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-2.5 text-stone-400">{i + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-stone-800">{row.name}</td>
                    <td className="px-4 py-2.5 text-stone-500">{row.employeeId}</td>
                    <td className="px-4 py-2.5 text-stone-500 uppercase text-xs">{row.department}</td>
                    <td className="px-4 py-2.5 font-mono text-stone-700">{row.loginTime}</td>
                    <td className="px-4 py-2.5 font-mono text-orange-600">{row.logoutTime || '-'}</td>
                    <td className="px-4 py-2.5">{statusBadge(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── EOD Location Report ─────────────────────────────────────────────────────
function EODReportView({ token }) {
  const todayIST = () => {
    const d = new Date(Date.now() + (5.5 * 60 + new Date().getTimezoneOffset()) * 60000);
    return d.toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(todayIST());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/location/eod-report?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [token, date]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleExpand = (email) => setExpanded(prev => ({ ...prev, [email]: !prev[email] }));

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#F8FAFC]" data-testid="eod-report-view">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-purple-600" />
          <h2 className="text-sm font-bold text-stone-800">End-of-Day Location Report</h2>
          <span className="text-[11px] text-stone-400">Per-employee location timeline with addresses</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            data-testid="eod-report-date"
            className="px-3 py-1.5 text-xs border border-stone-300 rounded-md outline-none focus:ring-2 focus:ring-purple-400 bg-white"
          />
          <button onClick={fetchReport} className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium">Refresh</button>
        </div>
      </div>

      {report && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
          {[
            { l: 'Total Employees', v: report.total, c: 'text-stone-700' },
            { l: 'Tracked Today', v: report.tracked, c: 'text-green-600' },
            { l: 'No Data', v: report.untracked, c: 'text-stone-400' },
          ].map(s => (
            <div key={s.l} className="bg-white border border-stone-200 rounded-xl p-3 text-center">
              <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-wide">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-stone-400 text-sm">Loading EOD report…</div>
      ) : !report || report.report.length === 0 ? (
        <div className="p-8 text-center text-stone-400 text-sm">No data for this date.</div>
      ) : (
        <div className="space-y-2">
          {report.report.map((emp) => (
            <div key={emp.email} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              {/* Employee header row */}
              <button
                onClick={() => toggleExpand(emp.email)}
                className="w-full flex items-start sm:items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left"
                data-testid={`eod-emp-${emp.employeeId}`}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ background: emp.hasData ? '#7c3aed' : '#94a3b8' }}>
                  {emp.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-stone-800">{emp.name}</p>
                    <span className="text-[10px] text-stone-400">{emp.employeeId} · {emp.department?.toUpperCase()}</span>
                    {emp.hasData ? (
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">{emp.totalPoints} pts</span>
                    ) : (
                      <span className="text-[10px] bg-stone-100 text-stone-400 px-1.5 py-0.5 rounded-full">No data</span>
                    )}
                  </div>
                  {emp.hasData && (
                    <div className="mt-0.5 text-[11px] text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      {emp.firstAddress && <span><span className="text-green-600 font-medium">First:</span> {emp.firstAddress}</span>}
                      {emp.lastAddress && emp.lastAddress !== emp.firstAddress && (
                        <span><span className="text-orange-600 font-medium">Last:</span> {emp.lastAddress}</span>
                      )}
                    </div>
                  )}
                </div>
                <span className="text-stone-400 text-lg leading-none flex-shrink-0">{expanded[emp.email] ? '▲' : '▼'}</span>
              </button>

              {/* Timeline expand */}
              {expanded[emp.email] && emp.hasData && (
                <div className="border-t border-stone-100 px-4 py-3 bg-stone-50">
                  <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-2">Full Timeline for {date}</p>
                  <div className="space-y-0">
                    {emp.placesVisited.length > 0 ? (
                      emp.placesVisited.map((p, i) => (
                        <div key={i} className="flex items-start gap-3 py-1.5 border-b border-stone-100 last:border-0">
                          <span className="text-[10px] text-stone-400 font-mono w-14 flex-shrink-0 pt-0.5">
                            {formatTime(p.time)}
                          </span>
                          <div className="flex items-start gap-1.5 min-w-0">
                            <MapPin className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-[12px] text-stone-700 leading-snug">{p.address}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-[11px] text-stone-400">GPS coordinates recorded but no address available.</p>
                    )}
                  </div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${emp.timeline[emp.timeline.length-1]?.lat},${emp.timeline[emp.timeline.length-1]?.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 py-1 px-3 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-medium hover:bg-blue-100 transition-colors"
                  >
                    <MapPin className="w-3 h-3" /> View last location in Google Maps
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── HR Dashboard — Map of ALL employees ─────────────────────────────────────
function HRLocationDashboard({ token }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersRef = useRef({});
  const polylineRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'list'
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Initialise Leaflet map — runs AFTER loading=false so the DOM container exists
  useEffect(() => {
    if (loading || !mapRef.current || leafletMap.current) return;
    const map = L.map(mapRef.current, { zoomControl: true }).setView(PUNE_CENTER, 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    leafletMap.current = map;
    // Allow the DOM to fully render before invalidating size
    setTimeout(() => map.invalidateSize(), 100);
    return () => { map.remove(); leafletMap.current = null; };
  }, [loading]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/location/all-employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(data.employees || []);
      setLastRefresh(new Date());
    } catch {
      // silently fail on poll
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Poll every 30 seconds
  useEffect(() => {
    fetchEmployees();
    const t = setInterval(fetchEmployees, 30_000);
    return () => clearInterval(t);
  }, [fetchEmployees]);

  // Update markers on map
  useEffect(() => {
    if (!leafletMap.current) return;
    const map = leafletMap.current;
    // Ensure map fills container
    map.invalidateSize();

    // Remove old markers
    Object.values(markersRef.current).forEach(m => m.remove());
    markersRef.current = {};

    employees.forEach(emp => {
      if (!emp.lat || !emp.lng) return;
      const color = deptColor(emp.department);
      const isOnline = emp.isOnline && (Date.now() - new Date(emp.timestamp)) < 30 * 60 * 1000;
      const icon = L.divIcon({
        className: '',
        html: `
          <div style="position:relative;width:40px;height:40px;cursor:pointer">
            <div style="
              width:40px;height:40px;border-radius:50%;
              background:${color};border:3px solid white;
              box-shadow:0 2px 8px rgba(0,0,0,0.3);
              display:flex;align-items:center;justify-content:center;
              font-size:14px;font-weight:700;color:white;
            ">${emp.name?.charAt(0) || '?'}</div>
            <span style="
              position:absolute;bottom:-1px;right:-1px;
              width:12px;height:12px;border-radius:50%;
              background:${isOnline ? '#22c55e' : '#94a3b8'};
              border:2px solid white;
            "></span>
          </div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -44],
      });

      const marker = L.marker([emp.lat, emp.lng], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="font-family:sans-serif;min-width:160px">
            <strong style="font-size:14px">${emp.name}</strong><br/>
            <span style="color:#64748b;font-size:12px">${emp.department?.toUpperCase()} · ${emp.employeeId}</span><br/>
            <span style="color:#64748b;font-size:11px">Last seen: ${timeSince(emp.timestamp)}</span><br/>
            ${emp.address ? `<span style="font-size:11px;color:#374151">${emp.address}</span>` : ''}
          </div>`);

      marker.on('click', () => selectEmployee(emp));
      markersRef.current[emp.email] = marker;
    });
  }, [employees]);

  const selectEmployee = useCallback(async (emp) => {
    setSelected(emp);
    // Zoom map to employee
    if (leafletMap.current && emp.lat) {
      leafletMap.current.flyTo([emp.lat, emp.lng], 15, { duration: 1 });
    }
    // Fetch today's timeline
    try {
      const { data } = await axios.get(`${API}/location/history/${encodeURIComponent(emp.email)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const hist = data.history || [];
      setTimeline(hist);
      // Draw polyline route on map
      if (polylineRef.current) { polylineRef.current.remove(); polylineRef.current = null; }
      if (hist.length >= 2) {
        const latlngs = hist.map(h => [h.lat, h.lng]);
        polylineRef.current = L.polyline(latlngs, {
          color: deptColor(emp.department), weight: 3, opacity: 0.7, dashArray: '6 4'
        }).addTo(leafletMap.current);
      }
    } catch { setTimeline([]); }
  }, [token]);

  const clearSelection = () => {
    setSelected(null);
    setTimeline([]);
    if (polylineRef.current) { polylineRef.current.remove(); polylineRef.current = null; }
    if (leafletMap.current) leafletMap.current.flyTo(PUNE_CENTER, 12);
  };

  const onlineCount = employees.filter(e => e.isOnline && (Date.now() - new Date(e.timestamp)) < 30 * 60 * 1000).length;

  // Filter employees by name/employeeId/department for the sidebar list
  const q = searchQuery.trim().toLowerCase();
  const filteredEmployees = !q ? employees : employees.filter(emp => {
    const hay = `${emp.name||''} ${emp.employeeId||''} ${emp.department||''} ${emp.email||''}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="flex flex-col h-full min-h-screen bg-[#F8FAFC]" data-testid="hr-location-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 bg-white border-b border-stone-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center shadow">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-stone-800">Live Location Tracker</h1>
            <p className="text-xs text-stone-400">
              {onlineCount} online · {employees.length} total tracked
              {lastRefresh && ` · Updated ${timeSince(lastRefresh.toISOString())}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="hidden sm:flex border border-stone-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors
                ${viewMode === 'map' ? 'bg-purple-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
            >
              <MapPin className="w-3.5 h-3.5" /> Map
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors
                ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setViewMode('logins')}
              data-testid="login-report-toggle"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors
                ${viewMode === 'logins' ? 'bg-purple-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
            >
              <Clock className="w-3.5 h-3.5" /> Logins
            </button>
            <button
              onClick={() => setViewMode('eod')}
              data-testid="eod-report-toggle"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors
                ${viewMode === 'eod' ? 'bg-purple-600 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
            >
              <FileText className="w-3.5 h-3.5" /> EOD Report
            </button>
          </div>
          <button
            onClick={() => fetchEmployees()}
            className="p-2 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors"
            data-testid="hr-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-stone-500" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-stone-400">
            <div className="w-8 h-8 border-3 border-stone-200 border-t-purple-600 rounded-full animate-spin border-[3px]" />
            <span className="text-sm">Loading employee locations…</span>
          </div>
        </div>
      ) : viewMode === 'logins' ? (
        <LoginReportView token={token} />
      ) : viewMode === 'eod' ? (
        <EODReportView token={token} />
      ) : (
        <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 72px)' }}>
          {/* Left: Employee list (always visible on mobile in list mode, sidebar on desktop) */}
          <div className={`
            ${viewMode === 'list' ? 'flex' : 'hidden sm:flex'}
            flex-col w-full sm:w-72 lg:w-80 border-r border-stone-200 bg-white overflow-y-auto flex-shrink-0
          `}>
            <div className="p-3 border-b border-stone-100">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                All Employees ({employees.length})
              </p>
              {/* Name search */}
              <div className="relative">
                <svg className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" strokeWidth="2"/>
                  <path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by name, ID, dept…"
                  data-testid="hr-location-search-input"
                  className="w-full pl-7 pr-7 py-1.5 text-xs border border-stone-300 rounded-md outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    data-testid="hr-location-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 text-lg leading-none"
                    title="Clear">×</button>
                )}
              </div>
              {searchQuery && (
                <p className="text-[10px] text-purple-600 mt-1.5 font-medium">
                  {filteredEmployees.length} of {employees.length} match "{searchQuery}"
                </p>
              )}
            </div>
            {filteredEmployees.length === 0 ? (
              <div className="p-6 text-center text-stone-400 text-sm">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                {searchQuery ? <>No employees match "<strong>{searchQuery}</strong>"</> : <>No location data yet.<br />Employees need to share their location.</>}
              </div>
            ) : (
              filteredEmployees
                .sort((a, b) => (b.isOnline ? 1 : 0) - (a.isOnline ? 1 : 0))
                .map(emp => {
                  const isOnline = emp.isOnline && (Date.now() - new Date(emp.timestamp)) < 30 * 60 * 1000;
                  const color = deptColor(emp.department);
                  return (
                    <button
                      key={emp.email}
                      onClick={() => { setViewMode('map'); selectEmployee(emp); }}
                      className={`flex items-center gap-3 px-4 py-3 border-b border-stone-50 hover:bg-stone-50 transition-colors text-left w-full
                        ${selected?.email === emp.email ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''}`}
                      data-testid={`emp-location-item-${emp.employeeId}`}
                    >
                      <div className="relative flex-shrink-0">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white shadow"
                          style={{ background: color }}>
                          {emp.name?.charAt(0) || '?'}
                        </div>
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white
                          ${isOnline ? 'bg-green-500' : 'bg-slate-300'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-800 truncate">{emp.name}</p>
                        <p className="text-xs text-stone-500 truncate">{emp.department?.toUpperCase()} · {emp.employeeId}</p>
                        {emp.address ? (
                          <p className="text-[10px] text-blue-600 truncate font-medium mt-0.5" title={emp.address}>
                            <MapPin className="inline w-2.5 h-2.5 mr-0.5" />{emp.address}
                          </p>
                        ) : (
                          <p className="text-[10px] text-stone-400">
                            {isOnline ? <span className="text-green-600 font-medium">● Online</span> : '● Offline'} · {timeSince(emp.timestamp)}
                          </p>
                        )}
                      </div>
                      <Eye className="w-4 h-4 text-stone-300 flex-shrink-0" />
                    </button>
                  );
                })
            )}
          </div>

          {/* Right: Map + timeline */}
          <div className={`flex-1 flex flex-col ${viewMode === 'list' ? 'hidden sm:flex' : 'flex'}`}>
            {/* Map */}
            <div className="relative flex-1" style={{ minHeight: '400px' }}>
              <div ref={mapRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, minHeight: '400px' }} />

              {/* Selected employee info overlay */}
              {selected && (
                <div className="absolute top-3 left-3 right-3 sm:left-auto sm:right-3 sm:w-72 z-[999] bg-white rounded-xl shadow-lg border border-stone-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ background: deptColor(selected.department) }}>
                        {selected.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-800">{selected.name}</p>
                        <p className="text-xs text-stone-500">{selected.employeeId} · {selected.department}</p>
                      </div>
                    </div>
                    <button onClick={clearSelection} className="text-stone-400 hover:text-stone-600 text-lg leading-none px-1">×</button>
                  </div>
                  {/* Today's timeline */}
                  <div className="max-h-40 overflow-y-auto">
                    <p className="text-[10px] font-semibold text-stone-500 uppercase mb-1 tracking-wider">Today's Timeline</p>
                    {timeline.length === 0 ? (
                      <p className="text-xs text-stone-400 py-2 text-center">No movement data for today</p>
                    ) : (
                      timeline.map((t, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs py-1.5 border-b border-stone-50 last:border-0">
                          <span className="text-stone-400 font-mono text-[10px] mt-0.5 w-12 flex-shrink-0">
                            {formatTime(t.timestamp)}
                          </span>
                          <div className="text-stone-600 leading-tight min-w-0">
                            {t.address ? (
                              <p className="text-[11px] text-blue-700 font-medium leading-snug">{t.address}</p>
                            ) : (
                              <span className="font-medium text-[10px] text-stone-400">{t.lat?.toFixed(5)}, {t.lng?.toFixed(5)}</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Open in Google Maps */}
                  {selected.lat && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${selected.lat},${selected.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors"
                    >
                      <MapPin className="w-3.5 h-3.5" /> Open in Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Employee View — Auto location tracking ───────────────────────────────────
function EmployeeLocationView({ user, token }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markerRef = useRef(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const coordsRef = useRef(null); // always up-to-date coords for interval

  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState('');
  const [timeline, setTimeline] = useState([]);
  const [status, setStatus] = useState('requesting'); // auto-starts immediately
  const [lastSent, setLastSent] = useState(null);
  const [inWindow, setInWindow] = useState(isWithinWindow());

  // Re-check window every minute
  useEffect(() => {
    const t = setInterval(() => setInWindow(isWithinWindow()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Initialise mini map
  useEffect(() => {
    if (!mapRef.current || leafletMap.current) return;
    const map = L.map(mapRef.current, { zoomControl: false }).setView(PUNE_CENTER, 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors', maxZoom: 19,
    }).addTo(map);
    leafletMap.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    return () => { map.remove(); leafletMap.current = null; };
  }, []);

  // Reverse geocode
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'DBK-CRM/1.0' } }
      );
      const data = await res.json();
      return data.display_name || '';
    } catch { return ''; }
  };

  const sendLocation = useCallback(async (lat, lng, acc) => {
    const addr = await reverseGeocode(lat, lng);
    setAddress(addr);
    try {
      await axios.post(`${API}/location/update`, {
        lat, lng, accuracy: acc, address: addr,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setLastSent(new Date());
      const { data } = await axios.get(`${API}/location/my-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeline(data.history || []);
    } catch { /* silent */ }
  }, [token]);

  const updateMapMarker = useCallback((lat, lng) => {
    if (!leafletMap.current) return;
    if (markerRef.current) markerRef.current.remove();
    const icon = L.divIcon({
      className: '',
      html: `<div style="width:20px;height:20px;border-radius:50%;background:#8b5cf6;border:3px solid white;box-shadow:0 2px 8px rgba(139,92,246,0.5)"></div>`,
      iconSize: [20, 20], iconAnchor: [10, 10],
    });
    markerRef.current = L.marker([lat, lng], { icon }).addTo(leafletMap.current);
    leafletMap.current.flyTo([lat, lng], 15, { duration: 1 });
  }, []);

  // Auto-start tracking on mount — no button needed
  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('unavailable');
      return;
    }

    const doStart = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lng, accuracy: acc } = pos.coords;
          coordsRef.current = { lat, lng, acc };
          setCoords({ lat, lng, acc });
          setStatus('active');
          updateMapMarker(lat, lng);
          await sendLocation(lat, lng, acc);

          // Live updates via watchPosition
          watchIdRef.current = navigator.geolocation.watchPosition(
            (p) => {
              const { latitude: la, longitude: lo, accuracy: ac } = p.coords;
              coordsRef.current = { lat: la, lng: lo, acc: ac };
              setCoords({ lat: la, lng: lo, acc: ac });
              updateMapMarker(la, lo);
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 30000 }
          );

          // Heartbeat every 60 s using ref to avoid stale closure
          intervalRef.current = setInterval(async () => {
            const c = coordsRef.current;
            if (c) await sendLocation(c.lat, c.lng, c.acc);
          }, 60_000);
        },
        () => { setStatus('denied'); },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );
    };

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((res) => {
        if (res.state !== 'denied') doStart();
        else setStatus('denied');
        res.onchange = () => { if (res.state === 'granted') doStart(); };
      }).catch(() => doStart());
    } else {
      doStart();
    }

    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load today's timeline on mount
  useEffect(() => {
    axios.get(`${API}/location/my-history`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setTimeline(r.data.history || [])).catch(() => {});
  }, [token]);

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="employee-location-view">
      {/* Header card */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-5 text-white mb-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-purple-200 text-sm mb-0.5">Live Location</p>
            <h1 className="text-xl font-bold">{user?.firstName} {user?.lastName}</h1>
            <p className="text-purple-300 text-xs mt-1">{user?.employeeId} · {user?.department?.toUpperCase()}</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Navigation className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Status badge */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {!inWindow ? (
            <span className="flex items-center gap-1.5 bg-amber-500/30 border border-amber-400/40 text-amber-200 text-xs px-3 py-1 rounded-full">
              <Clock className="w-3 h-3" /> Tracking inactive — resumes at 9:45 AM IST
            </span>
          ) : status === 'active' ? (
            <span className="flex items-center gap-1.5 bg-green-500/30 border border-green-400/40 text-green-200 text-xs px-3 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> Tracking Active
            </span>
          ) : status === 'requesting' ? (
            <span className="flex items-center gap-1.5 bg-yellow-500/20 border border-yellow-400/30 text-yellow-200 text-xs px-3 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" /> Starting…
            </span>
          ) : status === 'denied' ? (
            <span className="flex items-center gap-1.5 bg-red-500/30 border border-red-400/40 text-red-200 text-xs px-3 py-1 rounded-full">
              <WifiOff className="w-3 h-3" /> Location blocked — allow in browser settings
            </span>
          ) : (
            <span className="flex items-center gap-1.5 bg-white/10 border border-white/20 text-purple-200 text-xs px-3 py-1 rounded-full">
              <WifiOff className="w-3 h-3" /> Location unavailable
            </span>
          )}
          <span className="text-purple-300 text-xs">Window: 9:45 AM – 10:30 PM IST</span>
          {lastSent && inWindow && (
            <span className="text-purple-300 text-xs">Last sent: {formatTime(lastSent.toISOString())}</span>
          )}
        </div>
      </div>

      {/* Mini map */}
      <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm mb-5">
        <div ref={mapRef} style={{ height: '220px', width: '100%' }} />
      </div>

      {/* Current location / address */}
      {coords && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 mb-5 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
          <div>
            <p className="text-xs text-stone-500 font-medium mb-0.5">Current Location</p>
            {address ? (
              <p className="text-sm font-medium text-blue-700 leading-snug">{address}</p>
            ) : (
              <p className="text-sm font-mono text-stone-700">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</p>
            )}
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
          >
            <MapPin className="w-3.5 h-3.5" /> Open in Maps
          </a>
        </div>
      )}

      {/* Today's timeline */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-stone-400" />
            <span className="text-sm font-semibold text-stone-700">Today's Timeline</span>
          </div>
          <span className="text-xs text-stone-400">{timeline.length} entries</span>
        </div>
        {timeline.length === 0 ? (
          <div className="p-6 text-center text-stone-400 text-sm">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
            No location entries yet. Tracking will appear here shortly.
          </div>
        ) : (
          <div className="divide-y divide-stone-50 max-h-64 overflow-y-auto">
            {[...timeline].reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                <div className="w-14 text-center flex-shrink-0">
                  <span className="text-xs font-mono text-stone-500">{formatTime(entry.timestamp)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  {entry.address ? (
                    <p className="text-xs text-blue-700 font-medium leading-snug">{entry.address}</p>
                  ) : (
                    <p className="text-xs text-stone-600">{entry.lat?.toFixed(5)}, {entry.lng?.toFixed(5)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page Component ──────────────────────────────────────────────────────
export default function LocationTrackingPage() {
  const { user } = useOutletContext();
  const token = localStorage.getItem('crm_token');

  // Live location is visible ONLY to Assistant HR (Sahil Karale).
  // The system super_admin retains oversight. No other employee (not even the
  // HR Head) can view other employees' locations.
  const canViewAll =
    user?.role === 'super_admin' ||
    user?.employeeId === 'DBK-0014' ||
    (user?.email || '').toLowerCase() === 'assistant.hr@siddhivinayakinfra.estate';

  // HR/CMD see the full tracking dashboard; others see their own sharing view
  return canViewAll
    ? <HRLocationDashboard token={token} />
    : <EmployeeLocationView user={user} token={token} />;
}
